/**
 * `/admin/reports` — the registry view (SPEC §9.1).
 *
 * "Discovered reports, manifest hash, who can see each."
 *
 * Three states are worth calling out on this page because they are invisible
 * everywhere else:
 *
 * * **definition changed** — the stored `manifest_hash` differs from the
 *   running registry's, so the projection is behind the code;
 * * **not in registry** — a `reports` row whose package is gone. It is still
 *   grantable and cannot be opened, which is how a grant ends up pointing at
 *   nothing;
 * * **restricted** — excluded from every `all`-scope grant (§7.1).
 *
 * "Who can see each" is resolved through the same authorization code path the
 * report endpoints use, one resolution per active user. It is the slow way and
 * the correct one — see `report_registry_view`.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { Badge, Button, Card, Grid } from '../../components/admin/ui'
import { ErrorState, Skeleton } from '../../components/States'
import { adminApi } from '../../lib/admin'
import type { AdminReportRow, ReloadResult } from '../../lib/adminTypes'

function ReportActiveToggle({
  report,
  pending,
  onToggle,
}: {
  report: AdminReportRow
  pending: boolean
  onToggle: () => void
}) {
  const active = report.is_active
  const accent = report.department_accent ?? 'var(--accent)'
  return (
    <button
      type="button"
      disabled={pending}
      onClick={onToggle}
      aria-pressed={active}
      title={active ? 'Deactivate report' : 'Activate report'}
      className="inline-flex h-6 w-20 items-center rounded-full border border-[var(--color-rule)] bg-[var(--color-surface)] px-1 transition disabled:cursor-not-allowed disabled:opacity-60"
    >
      <span
        className={`h-4 w-4 rounded-full transition ${active ? 'translate-x-14' : 'translate-x-0'}`}
        style={{ backgroundColor: active ? accent : '#9CA3AF' }}
      />
      <span className="sr-only">{active ? 'Active' : 'Inactive'}</span>
    </button>
  )
}

export function AdminReports() {
  const queryClient = useQueryClient()
  const [lastReload, setLastReload] = useState<ReloadResult | null>(null)
  const reports = useQuery({ queryKey: ['admin', 'reports'], queryFn: adminApi.reports })
  const [changingSlug, setChangingSlug] = useState<string | null>(null)

  const reload = useMutation({
    mutationFn: adminApi.reloadReports,
    onSuccess: (result) => {
      setLastReload(result)
      void queryClient.invalidateQueries({ queryKey: ['admin'] })
      void queryClient.invalidateQueries({ queryKey: ['me'] })
      void queryClient.invalidateQueries({ queryKey: ['departments'] })
    },
  })

  const setActive = useMutation({
    mutationFn: (report: AdminReportRow) => {
      setChangingSlug(report.slug)
      return adminApi.setReportActive(report.slug, !report.is_active)
    },
    onSuccess: (rows) => {
      queryClient.setQueryData(['admin', 'reports'], rows)
      void queryClient.invalidateQueries({ queryKey: ['me'] })
      void queryClient.invalidateQueries({ queryKey: ['departments'] })
    },
    onSettled: () => {
      setChangingSlug(null)
    },
  })

  return (
    <Card
      title="Report registry"
      description="The code registry and its projection into the database, side by side."
      actions={
        <Button
          disabled={reload.isPending}
          onClick={() => {
            reload.mutate()
          }}
        >
          {reload.isPending ? 'Reloading…' : 'Reload from disk'}
        </Button>
      }
    >
      {reload.error !== null && (
        <div className="mb-3">
          <ErrorState error={reload.error} />
        </div>
      )}
      {lastReload !== null && (
        <p className="mb-3 rounded-[var(--radius-md)] border border-[var(--color-rule)] px-3 py-2 text-[length:var(--text-xs)]">
          <span className="numeric">{lastReload.registered.length}</span> registered,{' '}
          <span className="numeric">{lastReload.changed.length}</span> changed,{' '}
          <span className="numeric">{lastReload.deactivated.length}</span> deactivated
          {lastReload.deactivated.length > 0 && (
            <>
              {' '}
              (<span className="numeric">{lastReload.deactivated.join(', ')}</span>)
            </>
          )}
          .
        </p>
      )}

      {reports.isPending ? (
        <Skeleton rows={5} />
      ) : reports.error !== null ? (
        <ErrorState
          error={reports.error}
          onRetry={() => {
            void reports.refetch()
          }}
        />
      ) : (
        <Grid head={['Report', 'Department', 'Owner', 'Manifest hash', 'State', 'Who can see it']}>
          {reports.data.map((report) => (
            <tr key={report.slug} className="border-b border-[var(--color-rule)] last:border-0">
              <td className="px-2 py-1.5">
                <div className="font-medium">{report.title}</div>
                <div className="numeric text-[length:var(--text-2xs)] text-[var(--color-ink-muted)]">
                  {report.slug}
                </div>
              </td>
              <td className="px-2 py-1.5 text-[length:var(--text-xs)]" title={report.department}>
                {report.department_name ?? report.department}
              </td>
              <td className="px-2 py-1.5 text-[length:var(--text-xs)]">{report.owner ?? '—'}</td>
              <td
                className="numeric px-2 py-1.5 text-[length:var(--text-2xs)] text-[var(--color-ink-muted)]"
                title={report.manifest_hash ?? undefined}
              >
                {report.manifest_hash === null ? '—' : report.manifest_hash.slice(0, 12)}
              </td>
              <td className="px-2 py-1.5">
                <span className="flex flex-wrap items-center gap-2">
                  <ReportActiveToggle
                    report={report}
                    pending={changingSlug === report.slug}
                    onToggle={() => {
                      setActive.mutate(report)
                    }}
                  />
                  {report.restricted && <Badge tone="warning">restricted</Badge>}
                  {!report.is_active && <Badge tone="danger">inactive</Badge>}
                  {!report.in_registry && <Badge tone="danger">not in registry</Badge>}
                  {report.definition_changed && <Badge tone="warning">definition changed</Badge>}
                </span>
              </td>
              <td className="px-2 py-1.5 text-[length:var(--text-xs)]">
                {report.viewers.length === 0 ? (
                  <span className="text-[var(--color-ink-muted)]">nobody</span>
                ) : (
                  <details>
                    <summary className="cursor-pointer">
                      <span className="numeric">{report.viewers.length}</span>{' '}
                      {report.viewers.length === 1 ? 'user' : 'users'}
                    </summary>
                    <ul className="numeric mt-1 space-y-0.5 text-[length:var(--text-2xs)] text-[var(--color-ink-muted)]">
                      {report.viewers.map((upn) => (
                        <li key={upn}>{upn}</li>
                      ))}
                    </ul>
                  </details>
                )}
              </td>
            </tr>
          ))}
        </Grid>
      )}
    </Card>
  )
}
